import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector, useAppStore } from '~/store/hooks'
import { ExtrudeGeometry, Mesh, type Object3D, Shape, Vector2, Vector2Like, Vector3, Vector3Like } from 'three'
import { Circle, Html, Ring } from '@react-three/drei'
import { DEFAULT_WALL_SIZES } from '~/config/wallSizes'
import { deleteObjects } from '~/store/objects'
import { useDistanceDimensions } from '~/hooks/useDistanceDimensions'
import theme from '~/config/theme'
import { inputStyles } from '~/ui/Field'
import { SnapLine as LineToolSnapLine } from '~/components/DrawingCanvas/util/SnapLine'
import type { Line2 } from 'three/addons/lines/Line2.js'
import { CANVAS_BOUNDS } from '~/components/DrawingCanvas/Products/constants'
import { showAlert } from '~/store/alert'
import { setActiveTool } from '~/store/tools'
import { useMakeWall } from '~/components/DrawingCanvas/Walls/hooks/useMakeWall'
import { computeEdges, getSegmentShape } from '~/components/DrawingCanvas/Walls/util'
import { WallMesh } from '~/components/DrawingCanvas/Walls/components/WallMesh'
import { CURSOR_Z } from '~/components/DrawingCanvas/constants/zIndices'
import { useProject } from '~/components/DrawingCanvas/hooks'
import { ToolManagerRef } from '~/components/DrawingCanvas/ToolManager'
import { SnapPoint, snapToCardinals, snapToPointOrtho, snapToPoint } from '~/components/DrawingCanvas/util/snaplines'
import { modelToUI, vectorModelToUI, uiToModel } from '~/components/DrawingCanvas/util/units'

const SAME_POINT_EPSILON = 0.01
const MINIMUM_SEGMENT_LENGTH = 10
const POLYGON_CLOSE_THRESHOLD = 50
const CLICK_THRESHOLD = 10
const FLOOR = 0

function useMakeWallOnUnmount(points: Vector3[], thickness: number, extendedSegmentId?: string) {
  const layerKey = useAppSelector(state => state.layers.currentLayer)
  const makeWall = useMakeWall(layerKey)
  const store = useAppStore()
  const dispatch = useAppDispatch()

  useEffect(
    () => () => {
      const state = store.getState()
      const isLineTool = state.tools.activeTool === 'LINE_TOOL'
      const isSinglePoint = points.length < 2
      if (isLineTool || isSinglePoint) return
      if (layerKey === 'INTERIOR_WALLS') {
        makeWall(points)
        return
      }
      if (extendedSegmentId) {
        const extendedSegment = state.objects.present.segments[extendedSegmentId]
        const parentWall = state.objects.present.objects[extendedSegment?.parentId]
        const { segments } = state.objects.present
        const parentWallSegments = parentWall?.segments.map(id => segments[id]).filter(segment => !!segment)
        if (!parentWallSegments || !extendedSegment?.parentId) return
        const isExtendingFromWallStart =
          vectorModelToUI(parentWallSegments[0].startPoint).distanceToSquared(points[0]) < SAME_POINT_EPSILON
        const pointsCopy = [...points]
        if (isExtendingFromWallStart) {
          pointsCopy.reverse().pop()
        } else {
          pointsCopy.unshift()
        }
        const computePoints = parentWallSegments.reduce<Vector3[]>((pts, segment, i, { length }) => {
          const isFirst = i === 0
          if (isFirst && isExtendingFromWallStart) pts.push(...pointsCopy)
          pts.push(vectorModelToUI(segment.startPoint))
          const isLast = i === length - 1
          if (isLast) {
            pts.push(vectorModelToUI(segment.endPoint))
            if (!isExtendingFromWallStart) pts.push(...pointsCopy)
          }
          return pts
        }, [])
        makeWall(computePoints)
        dispatch(deleteObjects([extendedSegment.parentId]))
      }
    },
    [points, store, thickness, makeWall, dispatch]
  )
}

const cursorPoint = new Vector3()
const start = new Vector3()
const end = new Vector3()

const getFullCanvasSnaplinePoints = (a: Vector3Like, b: Vector3Like): [Vector3, Vector3] => {
  start.subVectors(a, b).normalize().multiplyScalar(CANVAS_BOUNDS).add(b)
  end.subVectors(b, a).normalize().multiplyScalar(CANVAS_BOUNDS).add(a)
  return [start, end]
}

export const WallLineTool = forwardRef<ToolManagerRef, object>(function WallLineTool(_, ref) {
  const isOrthoMode = useAppSelector(state => state.tools.isOrthoModeEnabled)
  const isSnapEnabled = useAppSelector(state => state.tools.isSnapEnabled)
  const layerKey = useAppSelector(state => state.layers.currentLayer)
  const is3D = useAppSelector(state => state.camera.is3D)
  const dispatch = useAppDispatch()
  const config =
    DEFAULT_WALL_SIZES[
      layerKey in DEFAULT_WALL_SIZES ? (layerKey as keyof typeof DEFAULT_WALL_SIZES) : 'EXTERIOR_WALLS'
    ]
  const thickness = modelToUI(config.thickness)
  const height = modelToUI(config.height)
  const cursorZ = is3D ? CURSOR_Z : height + 1
  const { displayFormattedValue } = useDistanceDimensions()
  const makeWall = useMakeWall(layerKey)
  const project = useProject()

  const [points, setPoints] = useState<Vector3[]>([])
  const extendedSegmentId = useRef<string>()
  const downPoint = useRef<Vector3 | null>(null)
  const cursor = useRef<Mesh>(null!)
  const snapline = useRef<Line2>(null!)
  const segmentPreview = useRef<Mesh>(null!)
  const dimension = useRef<HTMLDivElement>(null)
  const dimensionObj3D = useRef<Object3D>(null!)
  const ring = useRef<Mesh>(null!)

  const walls = useAppSelector(state => state.objects.present.objects)
  const segments = useAppSelector(state => state.objects.present.segments)
  const incompleteWallSnapPoints = useMemo(
    () =>
      Object.values(walls).reduce<SnapPoint[]>((points, wall) => {
        if (wall.layerKey !== layerKey) return points
        const firstSegment = segments[wall.segments[0]]
        const lastSegment = segments[wall.segments[wall.segments.length - 1]]
        const isIncompleteWall =
          wall.segments.length < 3 ||
          (firstSegment &&
            lastSegment &&
            new Vector3().copy(firstSegment.startPoint).distanceToSquared(lastSegment.endPoint) > SAME_POINT_EPSILON)
        if (isIncompleteWall) {
          points.push({ id: firstSegment.id, point: vectorModelToUI(firstSegment.startPoint) })
          points.push({ id: lastSegment.id, point: vectorModelToUI(lastSegment.endPoint) })
        }
        return points
      }, []),
    [walls, segments, layerKey]
  )
  const snapPoints = useMemo(
    () =>
      Object.values(segments).reduce<SnapPoint[]>((points, { id, startPoint, endPoint }) => {
        points.push({ id, point: vectorModelToUI(startPoint).setZ(FLOOR) })
        points.push({ id, point: vectorModelToUI(endPoint).setZ(FLOOR) })
        return points
      }, []),
    [segments]
  )

  const setCursorPosition = useCallback(
    (target: Vector3, base: Vector3 | undefined, isShiftKey: boolean) => {
      let snapVisualPosition:
        | { point: Vector3; segmentId?: string; isClosed?: boolean }
        | [Vector3, Vector3]
        | undefined = undefined

      const isOrtho = (isShiftKey || isOrthoMode) && base
      if (isOrtho) {
        const orthoPoint = snapToCardinals(base, target)
        target.copy(orthoPoint)
        snapVisualPosition = getFullCanvasSnaplinePoints(base, target)
      }

      if (isSnapEnabled) {
        const currentPoints = points.map(point => ({ id: '', point }))
        currentPoints.pop()
        const orthoSnapPoints = snapPoints.concat(currentPoints)
        const snapped = snapToPointOrtho(orthoSnapPoints, cursorPoint)
        if (snapped) {
          const { objectLine, snapPoint } = snapped
          snapVisualPosition = [objectLine.start, objectLine.end]
          cursorPoint.copy(snapPoint)
        }
      }

      const snappedToExistingBrokenSegment = snapToPoint(incompleteWallSnapPoints, target)
      if (snappedToExistingBrokenSegment && snappedToExistingBrokenSegment.objectID !== extendedSegmentId.current) {
        const { snapPoint, objectID } = snappedToExistingBrokenSegment
        snapVisualPosition = { point: snapPoint, segmentId: objectID }
        target.copy(snappedToExistingBrokenSegment.snapPoint)
      }

      const isClosingPolygon = points.length > 2 && points[0].distanceToSquared(target) < POLYGON_CLOSE_THRESHOLD
      if (isClosingPolygon && !extendedSegmentId.current) {
        snapVisualPosition = { point: points[0], isClosed: true }
        target.copy(points[0])
      }

      return snapVisualPosition
    },
    [isOrthoMode, isSnapEnabled, points, snapPoints, incompleteWallSnapPoints]
  )

  const hideVisualGuides = useCallback(() => {
    cursor.current.visible = true
    segmentPreview.current.visible = false
    ring.current.visible = false
    snapline.current.visible = false
    if (dimension.current) dimension.current.style.display = 'none'
  }, [])

  useMakeWallOnUnmount(points, thickness, extendedSegmentId.current)

  useImperativeHandle(
    ref,
    () => ({
      onMouseDown(event) {
        const intersection = project(event)
        if (!intersection) return true
        const { x, y } = intersection
        downPoint.current = new Vector3(x, y, FLOOR)
        return true
      },
      onMouseMove(event) {
        hideVisualGuides()
        const intersection = project(event)
        if (!intersection) return true
        const { x, y } = intersection
        cursorPoint.set(x, y, FLOOR)
        const base: Vector3 | undefined = points.length > 0 ? points[points.length - 1] : undefined
        const snapLinePosition = setCursorPosition(cursorPoint, base, event.shiftKey)

        if (Array.isArray(snapLinePosition)) {
          const [start, end] = snapLinePosition
          snapline.current.geometry.setPositions([...start.setZ(cursorZ).toArray(), ...end.setZ(cursorZ).toArray()])
          snapline.current.computeLineDistances()
          snapline.current.material.dashed = start.distanceToSquared(end) < CANVAS_BOUNDS ** 2
          snapline.current.visible = true
        } else if (snapLinePosition) {
          ring.current.position.copy(snapLinePosition.point).setZ(cursorZ)
          ring.current.visible = true
        }

        if (base && base.distanceToSquared(cursorPoint) > MINIMUM_SEGMENT_LENGTH) {
          const { insetEdges, outsetEdges } = computeEdges([base, cursorPoint], thickness)
          const [insetStart, insetEnd] = insetEdges[0]
          const [outsetStart, outsetEnd] = outsetEdges[0]
          const shape = getSegmentShape({
            insetEdge: { start: new Vector2(...insetStart), end: new Vector2(...insetEnd) },
            outsetEdge: { start: new Vector2(...outsetStart), end: new Vector2(...outsetEnd) },
          })
          segmentPreview.current.geometry.dispose()
          segmentPreview.current.geometry = new ExtrudeGeometry(shape, { depth: height })
          segmentPreview.current.visible = true
          dimensionObj3D.current.position.copy(cursorPoint).add(base).divideScalar(2)
          if (dimension.current) {
            dimension.current.style.display = 'block'
            dimension.current.textContent = displayFormattedValue(uiToModel(cursorPoint.distanceTo(base) - thickness))
          }
        }

        cursor.current.position.copy(cursorPoint).setZ(cursorZ)

        return true
      },
      onMouseUp(event) {
        const intersection = project(event)
        if (!intersection || !downPoint.current) return true

        const { x, y } = intersection
        const isClick = downPoint.current.distanceToSquared({ x, y, z: FLOOR }) < CLICK_THRESHOLD
        if (!isClick) {
          dispatch(showAlert({ text: 'Click to start placing wall points' }))
          return true
        }

        cursorPoint.set(x, y, FLOOR)
        const base: Vector3 | undefined = points.length > 0 ? points[points.length - 1] : undefined

        hideVisualGuides()

        const snappedPosition = setCursorPosition(cursorPoint, base, event.shiftKey)
        if (snappedPosition && !Array.isArray(snappedPosition) && snappedPosition.isClosed) {
          makeWall([...points, cursorPoint])
          dispatch(setActiveTool({ tool: 'SELECT_TOOL' }))
        } else if (snappedPosition && !Array.isArray(snappedPosition) && snappedPosition.segmentId) {
          const isStartingSegmentExtension = points.length === 0
          if (isStartingSegmentExtension) {
            setPoints([cursorPoint.clone()])
            extendedSegmentId.current = snappedPosition.segmentId
          } else {
            const parentId = segments[snappedPosition.segmentId]?.parentId
            const parentWallSegments = walls[parentId]?.segments.map(id => segments[id]).filter(segment => !!segment)
            if (!parentWallSegments) return true
            const parentWallDeletionIds = [parentId]
            const computePoints = parentWallSegments.reduce<Vector2Like[]>((pts, segment, i, { length }) => {
              const segmentStart = vectorModelToUI(segment.startPoint)
              const segmentEnd = vectorModelToUI(segment.endPoint)
              if (segment.id === snappedPosition.segmentId) {
                const secondaryWallId = segments[extendedSegmentId.current ?? '']?.parentId
                const isMergingTwoWalls = secondaryWallId && secondaryWallId !== parentId
                if (isMergingTwoWalls) parentWallDeletionIds.push(secondaryWallId)
                const secondaryWallPoints = isMergingTwoWalls
                  ? (walls[secondaryWallId]?.segments
                      .map(id => segments[id])
                      .filter(segment => !!segment)
                      .reduce<Vector3[]>((points, segment, i, { length }) => {
                        points.push(vectorModelToUI(segment.startPoint))
                        const isLast = i === length - 1
                        if (isLast) points.push(vectorModelToUI(segment.endPoint))
                        return points
                      }, []) ?? [])
                  : []
                const isConnectingSecondaryWallFromSegmentStart =
                  !!secondaryWallPoints.length &&
                  secondaryWallPoints[0].distanceToSquared(points[0]) < SAME_POINT_EPSILON
                if (isConnectingSecondaryWallFromSegmentStart) {
                  secondaryWallPoints.shift()
                } else {
                  secondaryWallPoints.pop()
                }

                const isWallStart = segmentStart.distanceTo(snappedPosition.point) < SAME_POINT_EPSILON
                if (isWallStart) {
                  pts.push(...secondaryWallPoints, ...points, segmentStart)
                  const isSingleSegmentWall = length === 1
                  if (isSingleSegmentWall) pts.push(segmentEnd)
                } else {
                  const pointsCopy = [...points].reverse()
                  pts.push(segmentStart, segmentEnd, ...pointsCopy, ...secondaryWallPoints)
                }
              } else {
                pts.push(segmentStart)
                const isLast = i === length - 1
                if (isLast) pts.push(segmentEnd)
              }
              return pts
            }, [])

            makeWall(computePoints)
            dispatch(deleteObjects(parentWallDeletionIds))
            dispatch(setActiveTool({ tool: 'SELECT_TOOL' }))
            extendedSegmentId.current = undefined
          }
        } else {
          setPoints(p => [...p, cursorPoint.clone()])
        }

        downPoint.current = null
        return true
      },
    }),
    [
      dispatch,
      project,
      cursorZ,
      points,
      thickness,
      height,
      walls,
      segments,
      displayFormattedValue,
      layerKey,
      setCursorPosition,
      hideVisualGuides,
    ]
  )

  const edges = points.length > 0 ? computeEdges(points, thickness) : null

  return (
    <>
      <Circle visible={false} ref={cursor} args={[2, 20]} material-color="pink" />
      <LineToolSnapLine ref={snapline} points={[start, end]} />
      <Ring visible={false} ref={ring} args={[8, 12]} material-color={theme.colors.three.objects.snapRegion.default} />
      <mesh ref={segmentPreview}>
        <extrudeGeometry args={[new Shape(), { depth: height }]} />
        <meshStandardMaterial color={0x888888} />
      </mesh>
      <object3D ref={dimensionObj3D}>
        <Html
          center
          style={{ display: 'none' }}
          ref={dimension}
          className={inputStyles({
            isOnCanvas: true,
            class: 'pointer-events-none',
          })}
        ></Html>
      </object3D>
      {edges && <WallMesh displayMeasures edges={edges} height={height} />}
    </>
  )
})
